import type Decimal from "decimal.js";
import { useCallback, useState } from "react";
import type { CurrencyPrimitive } from "@/api/cashier";
import { getDecimalSeparator } from "@/utils/helpers";
import {
  type AmountValidationError,
  useAmountValidation,
} from "./useAmountValidation";
import { useFormatAmount } from "./useFormatAmount";

type UseAmountInputOptions = {
  currency: CurrencyPrimitive;
  locale: string;
  min?: Decimal;
  max?: Decimal;
  maxDecimalPlaces?: number;
  onChange?: (amount: Decimal | undefined) => void;
  onError?: (
    error: AmountValidationError | "",
    input: string | undefined
  ) => void;
};

export type UseAmountInputResult = {
  value: string;
  error: string | undefined;
  /** Pass to <input onChange={(e) => handleChange(e.target.value)} /> */
  handleChange: (raw: string) => void;
  /** Programmatic set (e.g. MAX button). */
  setFromDecimal: (d: Decimal) => void;
};

export const useAmountInput = ({
  currency,
  locale,
  min,
  max,
  maxDecimalPlaces = 2,
  onChange,
  onError,
}: UseAmountInputOptions): UseAmountInputResult => {
  const [value, setValue] = useState<string>("");
  const [error, setError] = useState<string | undefined>();

  const runValidation = useAmountValidation({
    currency,
    locale,
    min,
    max,
    maxDecimalPlaces,
  });
  const { formatInput, formatSanitizedInput } = useFormatAmount();

  const handleChange = useCallback(
    (raw: string) => {
      const result = runValidation(raw);

      // Preserves a partially typed decimal tail (trailing separator or zeros) so it survives
      // reformatting, e.g. "5." or "5.10". Mirrors the display formatting of a valid in-progress input.
      const withDecimalTail = (sanitized: string) => {
        const decimalSep = getDecimalSeparator(locale);
        const separatorIdx = sanitized.indexOf(".");
        const intPart =
          separatorIdx === -1 ? sanitized : sanitized.slice(0, separatorIdx);
        const decTail =
          separatorIdx === -1 ? "" : sanitized.slice(separatorIdx);

        // in case of , locale the , is replaced with . in sanitization, for display replace it back.
        const decReplaced =
          decimalSep !== "." ? decTail.replace(".", decimalSep) : decTail;
        return formatInput(intPart, currency, locale) + decReplaced;
      };

      switch (result.status) {
        case "empty":
          setValue("");
          setError("");
          onChange?.(undefined);
          onError?.("", undefined);
          return;

        case "in-progress": {
          setValue(withDecimalTail(result.sanitized));
          setError("");
          onChange?.(result.decimal);
          return;
        }

        case "invalid":
          // For min/max errors the input is a well-formed number that is merely out of range, so keep
          // any partially typed decimal separator/tail. Only genuine format errors get fully reformatted.
          setValue(
            result.error === "format"
              ? formatInput(result.sanitized, currency, locale)
              : withDecimalTail(result.sanitized)
          );
          setError(result.message);
          onChange?.(undefined);
          onError?.(result.error, result.sanitized);
          return;

        case "valid":
          setValue(
            formatSanitizedInput(
              result.decimal,
              result.sanitized,
              currency,
              locale
            )
          );
          setError("");
          onChange?.(result.decimal);
          onError?.("", undefined);
          return;
      }
    },
    [
      runValidation,
      formatInput,
      formatSanitizedInput,
      getDecimalSeparator,
      currency,
      locale,
      onChange,
      onError,
    ]
  );

  const setFromDecimal = useCallback(
    (d: Decimal) => handleChange(d.toString()),
    [handleChange]
  );

  return { value, error, handleChange, setFromDecimal };
};
